import { useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Behavior } from "@/types/behavior.types";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface CreateBehaviorModalProps {
  isOpen: boolean;
  onClose: () => void;
  behavior: Behavior | null;
  agentId?: string;
  onBehaviorCreated: (behavior: Behavior) => void;
  onBehaviorUpdated: (behavior: Behavior) => void;
}

const behaviorSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
});

type FormValues = z.infer<typeof behaviorSchema>;

const CreateBehaviorModal = ({ 
  isOpen, 
  onClose, 
  behavior, 
  agentId,
  onBehaviorCreated, 
  onBehaviorUpdated 
}: CreateBehaviorModalProps) => {
  const { organization } = useAuth();
  const { toast } = useToast();
  const isEditing = !!behavior;

  const form = useForm<FormValues>({
    resolver: zodResolver(behaviorSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  // Reset form and load behavior data when modal opens or behavior changes
  useEffect(() => {
    if (isOpen) {
      if (behavior) {
        form.reset({
          title: behavior.title,
          description: behavior.description,
        });
      } else {
        form.reset({
          title: "",
          description: "",
        });
      }
    }
  }, [isOpen, behavior, form]);

  const onSubmit = async (data: FormValues) => {
    // For editing, we need the behavior to exist
    if (isEditing && !behavior) return;
    
    // For creating, we need both organization_id and agent_id since agent_id is NOT NULL in DB
    if (!isEditing && (!organization?.id || !agentId)) {
      toast({
        variant: "destructive",
        title: "Missing required data",
        description: "Both organization and agent are required to create behaviors.",
      });
      return;
    }

    try {
      if (isEditing && behavior) {
        // Update existing behavior
        const { data: updatedBehaviorData, error } = await supabase
          .from("behaviors")
          .update({
            title: data.title,
            description: data.description,
          })
          .eq("id", behavior.id)
          .select("*")
          .single();

        if (error) throw error;
        
        toast({
          title: "Behavior updated",
          description: "The behavior has been successfully updated.",
        });
        
        onBehaviorUpdated(updatedBehaviorData as Behavior);
      } else {
        // Create new behavior - both organization_id and agent_id are guaranteed to exist here
        const insertData = {
          title: data.title,
          description: data.description,
          organization_id: organization!.id, // Non-null assertion safe due to guard above
          agent_id: agentId! // Non-null assertion safe due to guard above
        };
        
        const { data: newBehaviorData, error } = await supabase
          .from("behaviors")
          .insert(insertData)
          .select("*")
          .single();

        if (error) throw error;
        
        toast({
          title: "Behavior created",
          description: "The behavior has been successfully created.",
        });
        
        onBehaviorCreated(newBehaviorData as Behavior);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: `Error ${isEditing ? "updating" : "creating"} behavior`,
        description: error.message || "An unexpected error occurred",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] bg-white">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Behavior" : "Create Behavior"}</DialogTitle>
          <DialogDescription>
            Define how your AI agent should behave in specific situations
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Be helpful with pricing questions"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="When users ask about pricing, provide clear and detailed information about our service plans..."
                      className="min-h-[300px] resize-y"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                {isEditing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBehaviorModal;